import React, { useState, useEffect, useCallback } from 'react';
import { supermercadoService, batchService, extractErrorDetails } from '../services/api';
import ErrorDisplay from './ErrorDisplay';
import { formatLocalDate, formatLocalDateTime } from '../utils/dateUtils';

function BatchStockUpdate() {
  const [supermercados, setSupermercados] = useState([]);
  const [selectedSupermercadoId, setSelectedSupermercadoId] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [errorTrace, setErrorTrace] = useState(null);
  const [debugInfo, setDebugInfo] = useState(null);
  const [success, setSuccess] = useState('');
  const [result, setResult] = useState(null);
  const [loadingSupermercados, setLoadingSupermercados] = useState(true);
  const [executionHistory, setExecutionHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [selectedHistory, setSelectedHistory] = useState(null);
  const [showHistoryDetail, setShowHistoryDetail] = useState(false);
  
  // Pagination and filters state
  const [historyFilters, setHistoryFilters] = useState({
    status: '',
    fromDate: '',
    toDate: '',
    sortBy: 'executionDate',
    sortDirection: 'desc'
  });
  const [historyPagination, setHistoryPagination] = useState({
    page: 0,
    size: 10,
    totalPages: 0,
    totalElements: 0
  });

  const fetchSupermercados = async () => {
    try {
      setLoadingSupermercados(true);
      setError('');
      setErrorTrace(null);
      setDebugInfo(null);
      const response = await supermercadoService.getActiveSupermercados();
      if (response.success) {
        setSupermercados(response.data || []);
      } else {
        setError(response.message || 'Error al cargar los supermercados');
        setErrorTrace(response.errorTrace || null);
      }
    } catch (err) {
      const { message, errorTrace: trace, debugInfo: debug } = extractErrorDetails(err, 'Error al cargar los supermercados');
      setError(message);
      setErrorTrace(trace);
      setDebugInfo(debug);
    } finally {
      setLoadingSupermercados(false);
    }
  };

  const fetchExecutionHistory = useCallback(async () => {
    try {
      setLoadingHistory(true);
      const filterParams = {
        ...historyFilters,
        page: historyPagination.page,
        size: historyPagination.size
      };
      
      // Clean empty values
      Object.keys(filterParams).forEach(key => {
        if (filterParams[key] === '') {
          delete filterParams[key];
        }
      });
      
      // Convert date strings to ISO DateTime format for the backend
      // Using local timezone to ensure correct date range
      if (filterParams.fromDate) {
        const fromDate = new Date(filterParams.fromDate + 'T00:00:00');
        filterParams.fromDate = new Date(fromDate.getTime() - fromDate.getTimezoneOffset() * 60000).toISOString();
      }
      if (filterParams.toDate) {
        const toDate = new Date(filterParams.toDate + 'T23:59:59');
        filterParams.toDate = new Date(toDate.getTime() - toDate.getTimezoneOffset() * 60000).toISOString();
      }
      
      const response = await batchService.getHistoryPaginated(filterParams);
      if (response.success) {
        const pageData = response.data;
        setExecutionHistory(pageData.content || []);
        setHistoryPagination(prev => ({
          ...prev,
          totalPages: pageData.totalPages,
          totalElements: pageData.totalElements
        }));
      } else {
        console.error('Error loading execution history:', response.message);
      }
    } catch (err) {
      console.error('Error loading execution history:', err);
    } finally {
      setLoadingHistory(false);
    }
  }, [historyFilters, historyPagination.page, historyPagination.size]);

  useEffect(() => {
    fetchSupermercados();
  }, []);

  useEffect(() => {
    fetchExecutionHistory();
  }, [historyFilters, historyPagination.page, fetchExecutionHistory]);

  const handleFilterChange = (field, value) => {
    setHistoryFilters(prev => ({
      ...prev,
      [field]: value
    }));
    setHistoryPagination(prev => ({ ...prev, page: 0 })); // Reset to first page when filters change
  };

  const handlePageChange = (newPage) => {
    setHistoryPagination(prev => ({ ...prev, page: newPage }));
  };

  const handleSortChange = () => {
    const newDirection = historyFilters.sortDirection === 'asc' ? 'desc' : 'asc';
    setHistoryFilters(prev => ({
      ...prev,
      sortDirection: newDirection
    }));
    setHistoryPagination(prev => ({ ...prev, page: 0 }));
  };

  const handleViewDetail = (history) => {
    setSelectedHistory(history);
    setShowHistoryDetail(true);
  };

  const handleCloseDetail = () => {
    setShowHistoryDetail(false);
    setSelectedHistory(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!selectedSupermercadoId) {
      setError('Por favor seleccione un supermercado');
      return;
    }

    setError('');
    setErrorTrace(null);
    setDebugInfo(null);
    setSuccess('');
    setResult(null);
    setLoading(true);

    try {
      const response = await batchService.batchUpdateStock(selectedSupermercadoId);
      
      if (response.success) {
        setSuccess(response.message || 'Actualización masiva de stock completada exitosamente');
        setResult(response.data);
        setSelectedSupermercadoId('');
        // Refresh execution history after successful batch
        fetchExecutionHistory();
      } else {
        setError(response.message || 'Error al iniciar la actualización masiva de stock');
        setErrorTrace(response.errorTrace || null);
      }
    } catch (err) {
      const { message, errorTrace: trace, debugInfo: debug } = extractErrorDetails(err, 'Error al iniciar la actualización masiva de stock');
      setError(message);
      setErrorTrace(trace);
      setDebugInfo(debug);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="batch-stock-update">
      <h2>Actualización Masiva de Stock por Supermercado</h2>
      <p className="info-text">
        Esta función permite iniciar una actualización masiva de stock para un supermercado específico.
      </p>

      <ErrorDisplay error={error} errorTrace={errorTrace} debugInfo={debugInfo} />
      {success && (
        <div className="success-section">
          <p className="success-message">{success}</p>
          {result && (
            <div className="result-details">
              <h3>Resultados de la Actualización</h3>
              <div className="stats-grid">
                <div className="stat-item">
                  <span className="stat-label">Total de filas procesadas:</span>
                  <span className="stat-value">{result.totalRows}</span>
                </div>
                <div className="stat-item">
                  <span className="stat-label">Productos actualizados:</span>
                  <span className="stat-value stat-success">{result.updated}</span>
                </div>
                <div className="stat-item">
                  <span className="stat-label">Productos no encontrados:</span>
                  <span className="stat-value stat-warning">{result.notFound}</span>
                </div>
              </div>
              
              {result.errorMessages && result.errorMessages.length > 0 && (
                <div className="messages-section error-messages">
                  <h4>Errores:</h4>
                  <ul>
                    {result.errorMessages.map((msg, index) => (
                      <li key={`err-${index}`}>{msg}</li>
                    ))}
                  </ul>
                </div>
              )}
              
              {result.notFoundMessages && result.notFoundMessages.length > 0 && (
                <div className="messages-section warning-messages">
                  <h4>Productos no encontrados:</h4>
                  <ul>
                    {result.notFoundMessages.map((msg, index) => (
                      <li key={`nf-${index}`}>{msg}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {loadingSupermercados ? (
        <p>Cargando supermercados...</p>
      ) : (
        <form onSubmit={handleSubmit} className="batch-update-form">
          <div className="input-group">
            <label htmlFor="supermercado">Seleccionar Supermercado</label>
            <select
              id="supermercado"
              value={selectedSupermercadoId}
              onChange={(e) => setSelectedSupermercadoId(e.target.value)}
              required
              disabled={loading}
            >
              <option value="">-- Seleccione un supermercado --</option>
              {supermercados.map((supermercado) => (
                <option key={supermercado.id} value={supermercado.id}>
                  {supermercado.nombre}
                </option>
              ))}
            </select>
          </div>

          <div className="form-actions">
            <button 
              type="submit" 
              className="btn btn-primary"
              disabled={loading || !selectedSupermercadoId}
            >
              {loading ? 'Procesando...' : 'Iniciar Actualización'}
            </button>
          </div>
        </form>
      )}

      {/* Execution History Section */}
      <div className="execution-history-section" style={{ marginTop: '40px' }}>
        <h3>Historial de Ejecuciones</h3>
        
        {/* Filters */}
        <div className="history-filters" style={{ marginBottom: '20px', display: 'flex', gap: '15px', flexWrap: 'wrap' }}>
          <div className="filter-group">
            <label htmlFor="status-filter">Estado:</label>
            <select
              id="status-filter"
              value={historyFilters.status}
              onChange={(e) => handleFilterChange('status', e.target.value)}
              style={{ marginLeft: '5px' }}
            >
              <option value="">Todos</option>
              <option value="SUCCESS">Exitoso</option>
              <option value="ERROR">Error</option>
            </select>
          </div>
          
          <div className="filter-group">
            <label htmlFor="fromDate-filter">Desde:</label>
            <input
              type="date"
              id="fromDate-filter"
              value={historyFilters.fromDate}
              onChange={(e) => handleFilterChange('fromDate', e.target.value)}
              style={{ marginLeft: '5px' }}
            />
          </div>
          
          <div className="filter-group">
            <label htmlFor="toDate-filter">Hasta:</label>
            <input
              type="date"
              id="toDate-filter"
              value={historyFilters.toDate}
              onChange={(e) => handleFilterChange('toDate', e.target.value)}
              style={{ marginLeft: '5px' }}
            />
          </div>
        </div>
        
        {loadingHistory ? (
          <p>Cargando historial...</p>
        ) : executionHistory.length === 0 ? (
          <p className="info-text">No hay ejecuciones registradas con los filtros seleccionados.</p>
        ) : (
          <>
            <div className="history-list">
              <table className="history-table">
                <thead>
                  <tr>
                    <th onClick={handleSortChange} style={{ cursor: 'pointer' }}>
                      Fecha de Ejecución {historyFilters.sortDirection === 'asc' ? '▲' : '▼'}
                    </th>
                    <th>Supermercado</th>
                    <th>Período (Desde - Hasta)</th>
                    <th>Estado</th>
                    <th>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {executionHistory.map((history) => (
                    <tr key={history.id}>
                      <td>{formatLocalDateTime(history.executionDate)}</td>
                      <td>{history.supermercadoNombre}</td>
                      <td>
                        {formatLocalDate(history.dateFrom)} - {formatLocalDate(history.dateTo)}
                      </td>
                      <td>
                        <span className={`status-badge status-${history.status.toLowerCase()}`}>
                          {history.status === 'SUCCESS' ? 'Exitoso' : 'Error'}
                        </span>
                      </td>
                      <td>
                        <button
                          type="button"
                          className="btn btn-sm btn-secondary"
                          onClick={() => handleViewDetail(history)}
                        >
                          Ver Detalle
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            
            {/* Pagination controls */}
            {historyPagination.totalPages > 1 && (
              <div className="pagination-controls" style={{ marginTop: '20px', display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '10px' }}>
                <button
                  onClick={() => handlePageChange(0)}
                  disabled={historyPagination.page === 0}
                  className="btn btn-sm"
                >
                  {'<<'}
                </button>
                <button
                  onClick={() => handlePageChange(historyPagination.page - 1)}
                  disabled={historyPagination.page === 0}
                  className="btn btn-sm"
                >
                  {'<'}
                </button>
                <span>
                  Página {historyPagination.page + 1} de {historyPagination.totalPages}
                  {' '}({historyPagination.totalElements} total)
                </span>
                <button
                  onClick={() => handlePageChange(historyPagination.page + 1)}
                  disabled={historyPagination.page >= historyPagination.totalPages - 1}
                  className="btn btn-sm"
                >
                  {'>'}
                </button>
                <button
                  onClick={() => handlePageChange(historyPagination.totalPages - 1)}
                  disabled={historyPagination.page >= historyPagination.totalPages - 1}
                  className="btn btn-sm"
                >
                  {'>>'}
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* History Detail Modal */}
      {showHistoryDetail && selectedHistory && (
        <div className="modal-overlay" onClick={handleCloseDetail}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Detalle de Ejecución</h3>
              <button className="close-button" onClick={handleCloseDetail}>×</button>
            </div>
            <div className="modal-body">
              <div className="detail-grid">
                <div className="detail-item">
                  <strong>Supermercado:</strong>
                  <span>{selectedHistory.supermercadoNombre}</span>
                </div>
                <div className="detail-item">
                  <strong>Fecha de Ejecución:</strong>
                  <span>{formatLocalDateTime(selectedHistory.executionDate)}</span>
                </div>
                <div className="detail-item">
                  <strong>Período Desde:</strong>
                  <span>{formatLocalDate(selectedHistory.dateFrom)}</span>
                </div>
                <div className="detail-item">
                  <strong>Período Hasta:</strong>
                  <span>{formatLocalDate(selectedHistory.dateTo)}</span>
                </div>
                <div className="detail-item">
                  <strong>Estado:</strong>
                  <span className={`status-badge status-${selectedHistory.status.toLowerCase()}`}>
                    {selectedHistory.status === 'SUCCESS' ? 'Exitoso' : 'Error'}
                  </span>
                </div>
              </div>

              {selectedHistory.status === 'SUCCESS' && selectedHistory.summary && (
                <div className="summary-section">
                  <h4>Resumen</h4>
                  <pre className="summary-text">{selectedHistory.summary}</pre>
                </div>
              )}

              {selectedHistory.status === 'ERROR' && selectedHistory.errorMessage && (
                <div className="error-section">
                  <h4>Mensaje de Error</h4>
                  <pre className="error-text">{selectedHistory.errorMessage}</pre>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={handleCloseDetail}>
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default BatchStockUpdate;
